// Typ: FindCircumradiusRightTriangleTask
// Temat: Okręgi w trójkącie → Okrąg opisany w trójkącie prostokątnym
// Wzory:  c^2 = a^2 + b^2,   R = c/2  (dla Δ prostokątnego; kąt C=90°)

import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import { InlineMath, BlockMath } from "react-katex";

// ===== helpers =====
const rnd = (a,b)=> Math.floor(Math.random()*(b-a+1))+a;

function gcd(a, b) {
  a = Math.abs(a); b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

function formatMixed(numer, denom) {
  const sign = Math.sign(numer) * Math.sign(denom);
  const N = Math.abs(numer), D = Math.abs(denom);
  const g = gcd(N, D);
  const n = N / g, d = D / g;
  if (d === 1) return `${sign * n}`;
  if (n < d) return `${sign < 0 ? "-" : ""}\\dfrac{${n}}{${d}}`;
  const whole = Math.floor(n / d), rem = n % d;
  if (rem === 0) return `${sign < 0 ? "-" : ""}${whole}`;
  return `${sign < 0 ? "-" : ""}${whole}\\dfrac{${rem}}{${d}}`;
}

// kilka trójek pitagorejskich do skalowania
const TRIPLES = [
  [3,4,5], [5,12,13], [8,15,17], [7,24,25],
  [9,40,41], [12,35,37], [20,21,29]
];

// ===== generator =====
export function generateTask() {
  const [x,y,z] = TRIPLES[rnd(0, TRIPLES.length-1)];
  const k = rnd(1, 3); // skala
  const a = x * k;
  const b = y * k;
  const c = z * k; // przeciwprostokątna
  return { type: "FindCircumradiusRightTriangleTask", data: { a, b, c } };
}

// ===== render task =====
export function renderTask({ data }) {
  const { a, b } = data;
  return (
    <div style={{ marginTop: 16, marginBottom: 8 }}>
      Dany jest trójkąt prostokątny o przyprostokątnych długości
      <InlineMath math={`\\ a=${a}`} /> oraz
      <InlineMath math={`\\ b=${b}`} />. Oblicz promień okręgu opisanego{" "}
      <span style={{ whiteSpace: "nowrap" }}>
        <InlineMath math={"R"} />.
      </span>
    </div>
  );
}

// ===== input =====
export function renderInput(_, userAnswer, onChange, disabled) {
  const val = userAnswer ?? { raw: "" };
  return (
    <div style={{ display:"grid", gap: 10, marginTop: 8 }}>
      <FractionInput
        value={val}
        onChange={onChange}
        disabled={disabled}
        label="R"
        placeholder="np. 7 1/2, 9/4, 6"
      />
    </div>
  );
}

// ===== check =====
export function checkAnswer({ data }, userAnswer) {
  const { c } = data;
  const expected = c / 2; // R = c/2

  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  const parsed =
    typeof userAnswer === "object" && userAnswer.parsed != null
      ? userAnswer.parsed
      : (typeof raw === "string" ? Number(raw.replace(",", ".")) : NaN);

  if (parsed == null || !Number.isFinite(parsed)) return false;
  // dopuszczamy dziesiętne ±0,01
  return Math.abs(parsed - expected) <= 0.01;
}

// ===== answer =====
export function renderAnswer({ data }) {
  const { c } = data;
  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`R = ${formatMixed(c, 2)}`} />
    </div>
  );
}

// ===== explanation =====
export function renderExplanation({ data }) {
  const { a, b, c } = data;
  return (
    <div style={{ textAlign:"left", lineHeight: 1.7 }}>
      <div><strong>Krok 1.</strong> W trójkącie prostokątnym mamy:</div>
      <BlockMath math={'c^2 = a^2 + b^2,\\quad R = \\dfrac{c}{2}'} />

      <div><strong>Krok 2.</strong> Liczymy przeciwprostokątną:</div>
      <BlockMath math={`c = \\sqrt{${a}^2 + ${b}^2} = ${c}`} />

      <div><strong>Krok 3.</strong> Podstawiamy do wzoru na promień:</div>
      <BlockMath math={`R = \\dfrac{${c}}{2}`} />

      <div><strong>Krok 4.</strong> Upraszczamy:</div>
      <BlockMath math={`R = ${formatMixed(c, 2)}`} />
    </div>
  );
}

// ===== user answer =====
export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  if (!raw || typeof raw !== "string" || raw.trim() === "") return <i>(brak odpowiedzi)</i>;
  return (
    <div style={{ marginTop: 8 }}>
      <InlineMath math={formatLatexPreview(raw, "R")} />
    </div>
  );
}
